export interface WallEndCap extends HalfEdgeOffsets, HalfEdgeTangents {
  readonly verts: number[];
  readonly isFreeEnd: boolean;
  readonly offsetDifferenceOutline?: number[]; // used to fill gaps created by walls of differing thickness that are parallel.
  readonly rightMiterExtend: boolean | undefined; // true = right, false = left, undefined = neither
}

/**
 * WallEndJoins for controlling WallEndCap geometry per vertex halfEdge
 */

export type WallEndJoin = WallEndMiterJoin | WallEndButtJoin;
export type WallSideEndJoin = WallEndMiterJoin | WallEndAlignedButtJoin; // no WallEndMiteredButtJoin for WallSides

export enum WallEndJoinType {
  MITER = "MITER",
  BUTT = "BUTT",
}

interface WallEndJoinBase<T extends WallEndJoinType> {
  readonly joinType: T;
}

export interface WallEndMiterJoin extends WallEndJoinBase<WallEndJoinType.MITER> {
  readonly shiftTJunction: boolean;
}

export type WallEndButtJoin = WallEndAlignedButtJoin | WallEndMiteredButtJoin;

export enum ButtCapMode {
  END_MITER_EXTEND = "END_MITER_EXTEND",
  END_ALIGNED = "END_ALIGNED",
}

interface WallEndButtJoinBase<T extends ButtCapMode> extends WallEndJoinBase<WallEndJoinType> {
  readonly buttCapMode: T;
}

export interface WallEndAlignedButtJoin extends WallEndButtJoinBase<ButtCapMode.END_ALIGNED> {
  readonly buttCapExtension: number;
  readonly buttCapWrap: number;
}

export interface WallEndMiteredButtJoin extends WallEndButtJoinBase<ButtCapMode.END_MITER_EXTEND> {
  readonly buttCapMaxPerpEdgeAligned: boolean;
  readonly buttCapDefault: number;
}

export enum WallEndSideCornerType {
  CONVEX = "CONVEX",
  CONCAVE = "CONCAVE",
  PARALLEL = "PARALLEL",
}

/**
 * WallEndCapSources are intermediate representations for the end caps that enable repairing
 * miters for complex room edge arrangements. `repairMitersOnSelfIntersectingRoomEdges` uses
 * these to representations to associate self-intersecting loops with miters and walls that
 * need to be repaired.
 *
 * Most of the types are self-explanatory.  The 'NoOpWallCapSource' type is used to represent VOID walls
 * only for the purpose of creating the polyLine for detecting self-intersections and removing
 * loops. 'NoOpWallCapSources` are not used for actual wall cap construction.
 *
 * Ultimately, all the types of WallEndCapSources (except `NoOp`) are used to create the polyLine for the
 * wall cap, but the intermediate representation is used to repair the miters.
 */
export type WallEndCapSource = NoOpWallCapSource | SolidWallEndCapSource;
export type SolidWallEndCapSource = FreeEndWallCapSource | MiterWallEndCapSource;
export type MiterWallEndCapSource =
  | LeftMiterExtendWallCapSource
  | RightMiterExtendWallCapSource
  | DefaultWallEndCapSource;

/**
 * WallEndCapSource MiterTypes are used for repairing miters,
 * but not describing the end condition.
 * @TODO - rename this and split the interfaces? FREE_END, DEFAULT and NO_OP are not really
 * miters and are more like MiterSources
 */
export enum EndCapSourceType {
  DEFAULT = "DEFAULT",
  FREE_END = "FREE_END",
  LEFT_MITER_EXTEND = "LEFT_MITER_EXTEND",
  RIGHT_MITER_EXTEND = "RIGHT_MITER_EXTEND",
  NO_OP = "NO_OP", // created by void wall caps and cannot be altered
}

interface WallEndCapSourceBase<T extends EndCapSourceType> {
  readonly halfEdge: number;
  readonly sourceType: T;
  readonly sx: number;
  readonly sy: number;
}

interface SolidWallEndCapSourceBase<T extends EndCapSourceType>
  extends WallEndCapSourceBase<T>,
    RightAndLeftMiters,
    RightAndLeftTangents {
  readonly leftWallOffset: number;
  readonly rightWallOffset: number;
}

/**
 * The most common wall end cap type defines a left (`lmx`,`lmy`) and a right miter (`rmx`,`rmy`)
 * in addition to the base wall end cap properties.
 */
export interface DefaultWallEndCapSource extends SolidWallEndCapSourceBase<EndCapSourceType.DEFAULT> {
  readonly omitStart?: boolean;
}

/**
 * This rare end cap type defines a left (`lmx`,`lmy`) and a right miter (`rmx`,`rmy`)
 * but represents a free end where a single wall terminates.
 */
export interface FreeEndWallCapSource extends SolidWallEndCapSourceBase<EndCapSourceType.FREE_END> {
  // deliberately empty
}

/**
 * This end cap type is created when two collinear walls of varying thickness are joined
 * and the left miter (`lmx`,`lmy`) is supplemented with an intermediate extension pt (`rx`,`ry`).
 */
export interface LeftMiterExtendWallCapSource extends SolidWallEndCapSourceBase<EndCapSourceType.LEFT_MITER_EXTEND> {
  readonly rx: number;
  readonly ry: number;
}

/**
 * This end cap type is created when two collinear walls of varying thickness are joined
 * and the right miter (`rmx`,`rmy`) is supplemented with an intermediate extension pt (`lx`,`ly`).
 * Additionally, due to the nature of room edge winding for cross section code, a
 * (`fillerlx`,`fillerly`) point is also added.
 */
export interface RightMiterExtendWallCapSource extends SolidWallEndCapSourceBase<EndCapSourceType.RIGHT_MITER_EXTEND> {
  readonly lx: number;
  readonly ly: number;
  readonly fillerlx: number;
  readonly fillerly: number;
}

/**
 * The 'NoOpWallCapSource' type is used to represent VOID walls only for the purpose of
 * creating the polyLine for detecting self-intersections and removing
 * loops. 'NoOpWallCapSources` are not used for actual wall cap construction.
 */
export interface NoOpWallCapSource extends WallEndCapSourceBase<EndCapSourceType.NO_OP> {
  // deliberately empty
}

// TODO - Comments

export interface WallEndCapSourceVertices {
  readonly halfEdge: number;
  readonly miterType: EndCapSourceType;
  readonly vertices: number[];
}

export interface RightAndLeftMiters {
  readonly lmx: number;
  readonly lmy: number;
  readonly rmx: number;
  readonly rmy: number;
}

interface RightAndLeftTangents {
  readonly ltx: number;
  readonly lty: number;
  readonly rtx: number;
  readonly rty: number;
}

export interface HalfEdgeOffsets {
  readonly rightOffset: number;
  readonly leftOffset: number;
}

/**
 * 
 * WallEndCapTangents are the vectors representing the line2 tangents of the planar graph edges
 * used to produce a WallEndCap. They point from the end cap vertex towards the mitering
 * vertices, regardless of room edge winding order.
 * 
 * It should be noted that in the event that there are only two edges at a vertex,
 * the left and right tangents both point in the same direction.
 * 
 * In the event that the wall is a "FREE_END", the tangents both point in the same
 * direction as the planar graph edge
 * 
 * ```
                                                                          
                                 planarGraph                             
                                 vertex                                  
                    ---------------- 0 ------------------                
 right miter planar graph edge     -/|\-     left miter planar graph edge
 produces rightTangents           /  |  \    produces leftTangents       
                                  0  |  0                                
                                  |  |  |                                
                                  |  |  |  0 = end cap vertices          
                                  |  |  |                                
                                  |  |  |                                
                                  |  |  |                                
                                  |  |  |                                
                                  |  |  |                                
                                  |  |  |                                
                                  |  |  |                                
                                  |  |  |                                
                        rightEdge |  |  | leftEdge                       
                                  |  |  |                                
                                  |  |  |                                
                         rightOffset | leftOffset                        
                                     |                                   
                                     |                                   
                                planarGraph                              
                                edge                                     

 * ```
 * 
 */

export interface HalfEdgeTangents {
  readonly leftTangentX: number;
  readonly leftTangentY: number;
  readonly rightTangentX: number;
  readonly rightTangentY: number;
}

/**
 * General interfaces for wallMass generation
 */

export type HalfEdgeToMiters = RightAndLeftMiters[]; // for debug purposes
export type HalfEdgeToWallEndCapSource = WallEndCapSource[];
